/**
 * Spec 217 PR3 — ClientDetail drilldown (`/clients/:slug`).
 *
 * Surface:
 *   - Header with client name + total works
 *   - Contact card: phone (tap-to-call via `tel:` link), address
 *     with "Copiar" button, NIF/CIF
 *   - List of associated works (sorted by updatedAt desc), each
 *     row links into the existing `/works/:id` detail page
 *
 * Failure modes:
 *   - Slug not found → render an empty-state card with a "Volver
 *     a Clientes" button. We don't redirect because the slug
 *     might appear later (e.g. user opened a stale tab while the
 *     work hadn't finished syncing).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Copy, Hash, MapPin, Phone, Users } from 'lucide-react';

import { EmptyState } from '../components/EmptyState';
import { SkeletonList } from '../components/Skeleton';
import { getClients, getWorks, subscribeStore } from '../store/db';
import type { WebIdentity } from '../store/keys';
import type { Client, Work } from '../types';
import { buildAgendaGroups, findClientBySlug } from '../utils/clients';

import './ClientDetail.css';

interface ClientDetailProps {
  identity: WebIdentity;
}

const STATUS_LABEL: Record<Work['status'], string> = {
  pending: 'Pendiente',
  in_progress: 'En curso',
  completed: 'Completado',
};

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function ClientDetail({ identity: _identity }: ClientDetailProps) {
  const { slug } = useParams<{ slug: string }>();
  const [clients, setClients] = useState<Client[] | null>(null);
  const [works, setWorks] = useState<Work[] | null>(null);
  const [copied, setCopied] = useState(false);

  // Agenda-anchored: must match the slug derivation in /clients
  // (`buildAgendaGroups`) so links from the list view always resolve.
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      void Promise.all([getClients(), getWorks(true)]).then(([cs, ws]) => {
        if (cancelled) return;
        setClients(cs);
        setWorks(ws);
      });
    };
    load();
    const unsub = subscribeStore(load);
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  const group = useMemo(() => {
    if (!clients || !works) return null;
    if (!slug) return undefined;
    const groups = buildAgendaGroups(clients, works);
    return findClientBySlug(groups, slug);
  }, [clients, works, slug]);

  // Newest-first list of works for this client.
  const sortedWorks = useMemo(() => {
    if (!group) return [];
    return [...group.works].sort((a, b) => b.updatedAt - a.updatedAt);
  }, [group]);

  function copyAddress() {
    if (!group?.address) return;
    void navigator.clipboard?.writeText(group.address).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => {
        // Clipboard denied — silently no-op. Future: add a toast.
      },
    );
  }

  if (works === null || clients === null) {
    return (
      <main id="main-content" className="cd-screen" data-testid="client-detail-screen">
        <SkeletonList count={4} testId="client-detail-loading" />
      </main>
    );
  }

  if (!group) {
    return (
      <main id="main-content" className="cd-screen" data-testid="client-detail-screen">
        <Link to="/clients" className="cd-back" aria-label="Volver a Clientes">
          <ChevronLeft size={20} strokeWidth={2} aria-hidden="true" />
          Volver a Clientes
        </Link>
        <EmptyState
          testId="client-detail-not-found"
          variant="noResults"
          title="Cliente no encontrado"
          body={
            <>
              No hay ningún cliente con esa URL. Quizá fue archivado todos los
              trabajos asociados — vuelve a la lista para verificarlo.
            </>
          }
        />
      </main>
    );
  }

  return (
    <main id="main-content" className="cd-screen" data-testid="client-detail-screen">
      <header className="cd-header">
        <Link to="/clients" className="cd-back" aria-label="Volver a Clientes" data-testid="client-detail-back">
          <ChevronLeft size={20} strokeWidth={2} aria-hidden="true" />
          Clientes
        </Link>
        <div className="cd-header-title">
          <Users size={24} strokeWidth={2} aria-hidden="true" />
          <h1 className="cd-h1" data-testid="client-detail-name">
            {group.name}
          </h1>
        </div>
        <p className="cd-header-subtitle">
          {group.total} {group.total === 1 ? 'trabajo' : 'trabajos'} activos
        </p>
      </header>

      {(group.phone || group.address || group.taxId) && (
        <section className="cd-contact" aria-label="Contacto">
          {group.phone && (
            <div className="cd-contact-row" data-testid="client-detail-phone-row">
              <Phone size={20} strokeWidth={2} aria-hidden="true" />
              <a
                href={`tel:${group.phone}`}
                className="cd-contact-value cd-contact-link"
                data-testid="client-detail-phone-link"
              >
                {group.phone}
              </a>
            </div>
          )}
          {group.address && (
            <div className="cd-contact-row" data-testid="client-detail-address-row">
              <MapPin size={20} strokeWidth={2} aria-hidden="true" />
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(group.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="cd-contact-value cd-contact-link"
                data-testid="client-detail-address-link"
              >
                {group.address}
              </a>
              <button
                type="button"
                className="cd-contact-action"
                onClick={copyAddress}
                aria-label="Copiar dirección"
                data-testid="client-detail-address-copy"
              >
                <Copy size={16} strokeWidth={2} aria-hidden="true" />
                {copied ? '¡Copiado!' : 'Copiar'}
              </button>
            </div>
          )}
          {group.taxId && (
            <div className="cd-contact-row" data-testid="client-detail-taxid-row">
              <Hash size={20} strokeWidth={2} aria-hidden="true" />
              <span className="cd-contact-value">{group.taxId}</span>
            </div>
          )}
        </section>
      )}

      <section className="cd-works" aria-label="Trabajos asociados">
        <h2 className="cd-h2">Trabajos</h2>
        <ul className="cd-works-list" data-testid="client-detail-works-list">
          {sortedWorks.map((work) => (
            <li key={work.id}>
              <Link
                to={`/works/${work.id}`}
                className={`cd-work-row cd-work-row--${work.status}`}
                data-testid={`client-detail-work-${work.id}`}
              >
                <div className="cd-work-row-main">
                  <span className="cd-work-row-title">{work.title}</span>
                  <span className="cd-work-row-meta">
                    {STATUS_LABEL[work.status]}
                    <span className="cd-work-row-sep">·</span>
                    Actualizado {formatDate(work.updatedAt)}
                  </span>
                </div>
                <ChevronRight size={20} strokeWidth={2} aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}
